// Copyright (c) Jupyter Development Team.
// Distributed under the terms of the Modified BSD License.
import { ObservableMap } from '@jupyterlab/observables';
import { Signal } from '@lumino/signaling';
import { EditorHandler } from '../handlers/editor';
import { nullTranslator } from '@jupyterlab/translation';
import { DebuggerPausedOverlay } from './pausedoverlay';
/**
 * A handler for consoles.
 */
export class ConsoleHandler {
    /**
     * Instantiate a new ConsoleHandler.
     *
     * @param options The instantiation options for a ConsoleHandler.
     */
    constructor(options) {
        var _a;
        this._debuggerService = options.debuggerService;
        this._consolePanel = options.widget;
        this._cellMap = new ObservableMap();
        this.translator = options.translator || nullTranslator;
        this._pausedOverlay = new DebuggerPausedOverlay({
            debuggerService: this._debuggerService,
            container: this._consolePanel.node,
            translator: this.translator
        });
        const codeConsole = this._consolePanel.console;
        if (codeConsole.promptCell) {
            this._addEditorHandler(codeConsole.promptCell);
        }
        codeConsole.promptCellCreated.connect((_, cell) => {
            this._addEditorHandler(cell);
        });
        const addHandlers = () => {
            for (const cell of codeConsole.cells) {
                this._addEditorHandler(cell);
            }
        };
        addHandlers();
        this._consolePanel.console.cells.changed.connect(addHandlers);
        // Connect debugger events
        (_a = this._debuggerService.session) === null || _a === void 0 ? void 0 : _a.eventMessage.connect((_, event) => {
            var _a, _b, _c;
            const session = this._debuggerService.session;
            const contextSession = this._consolePanel.sessionContext.session;
            if (!session || !contextSession) {
                return;
            }
            if (((_b = (_a = session.connection) === null || _a === void 0 ? void 0 : _a.kernel) === null || _b === void 0 ? void 0 : _b.id) !== ((_c = contextSession.kernel) === null || _c === void 0 ? void 0 : _c.id)) {
                return;
            }
            if (event.event === 'stopped') {
                void this._pausedOverlay.show();
            }
            else if (event.event === 'continued' || event.event === 'terminated') {
                void this._pausedOverlay.hide();
            }
        });
        // If already paused when initialized
        if (this._debuggerService.hasStoppedThreads()) {
            void this._pausedOverlay.show();
        }
    }
    /**
     * Dispose the handler.
     */
    dispose() {
        if (this.isDisposed) {
            return;
        }
        this.isDisposed = true;
        this._pausedOverlay.dispose();
        this._cellMap.values().forEach(handler => handler.dispose());
        this._cellMap.dispose();
        Signal.clearData(this);
    }
    /**
     * Add a new editor handler for the given cell.
     *
     * @param cell The cell to add the handler to.
     */
    _addEditorHandler(cell) {
        const modelId = cell.model.id;
        if (cell.model.type !== 'code' || this._cellMap.has(modelId)) {
            return;
        }
        const codeCell = cell;
        const editorHandler = new EditorHandler({
            debuggerService: this._debuggerService,
            editorReady: async () => {
                await codeCell.ready;
                return codeCell.editor;
            },
            getEditor: () => codeCell.editor,
            src: cell.model.sharedModel
        });
        codeCell.disposed.connect(() => {
            this._cellMap.delete(modelId);
            editorHandler.dispose();
        });
        this._cellMap.set(modelId, editorHandler);
    }
}
//# sourceMappingURL=console.js.map